import { useMemo, useRef, useState } from 'react';

export interface ChartPoint {
  date: string; // YYYY-MM-DD
  value: number;
}

interface Props {
  points: ChartPoint[];
  color: 'success' | 'danger' | 'accent' | 'neutral';
  /** Format value for tooltip (e.g. "12 345 USDT") */
  formatValue: (v: number) => string;
  /** Format date for tooltip (e.g. "9 мая") */
  formatDate?: (iso: string) => string;
  height?: number;
}

const COLOR_STROKE: Record<Props['color'], string> = {
  success: 'var(--green)',
  danger: 'var(--red)',
  accent: 'var(--accent)',
  neutral: 'var(--text2)',
};

const PADDING_TOP = 16;
const PADDING_BOTTOM = 28;
const PADDING_LEFT = 56;
const PADDING_RIGHT = 16;

const MONTHS_SHORT = [
  'янв',
  'фев',
  'мар',
  'апр',
  'мая',
  'июн',
  'июл',
  'авг',
  'сен',
  'окт',
  'ноя',
  'дек',
];

function formatShortDate(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return iso;
  return `${Number(m[3])} ${MONTHS_SHORT[Number(m[2]) - 1]}`;
}

export function Chart({ points, color, formatValue, formatDate = formatShortDate, height = 280 }: Props) {
  const [hover, setHover] = useState<number | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const [width, setWidth] = useState(800);
  const containerRef = useRef<HTMLDivElement>(null);

  // resize observer для адаптивной ширины
  useResizeObserver(containerRef, (w) => setWidth(w));

  const layout = useMemo(() => buildLayout(points, width, height), [points, width, height]);
  const stroke = COLOR_STROKE[color];

  if (points.length === 0) {
    return (
      <div className="flex items-center justify-center text-sm text-text2" style={{ height }}>
        Нет данных за период
      </div>
    );
  }

  function onMouseMove(e: React.MouseEvent<SVGSVGElement>) {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = e.clientX - rect.left;
    const innerX = ((x - PADDING_LEFT) / (width - PADDING_LEFT - PADDING_RIGHT)) * (points.length - 1);
    const idx = Math.max(0, Math.min(points.length - 1, Math.round(innerX)));
    setHover(idx);
  }

  return (
    <div ref={containerRef} className="relative w-full">
      <svg
        ref={svgRef}
        viewBox={`0 0 ${width} ${height}`}
        width="100%"
        height={height}
        className="block overflow-visible"
        onMouseMove={onMouseMove}
        onMouseLeave={() => setHover(null)}
      >
        <defs>
          <linearGradient id={`chart-${color}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={stroke} stopOpacity="0.22" />
            <stop offset="100%" stopColor={stroke} stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* Y-axis ticks + grid */}
        {layout.yTicks.map((t) => (
          <g key={t.value}>
            <line
              x1={PADDING_LEFT}
              x2={width - PADDING_RIGHT}
              y1={t.y}
              y2={t.y}
              stroke="var(--border-subtle)"
              strokeWidth={1}
            />
            <text
              x={PADDING_LEFT - 8}
              y={t.y + 3}
              textAnchor="end"
              className="fill-text2 font-mono text-[10px] tabular-nums"
            >
              {t.label}
            </text>
          </g>
        ))}

        {/* X-axis ticks */}
        {layout.xTicks.map((t) => (
          <text
            key={t.iso}
            x={t.x}
            y={height - PADDING_BOTTOM + 16}
            textAnchor="middle"
            className="fill-text2 font-mono text-[10px]"
          >
            {formatDate(t.iso)}
          </text>
        ))}

        {/* Area + line */}
        <path d={layout.area} fill={`url(#chart-${color})`} />
        <path
          d={layout.line}
          fill="none"
          stroke={stroke}
          strokeWidth={2}
          strokeLinejoin="round"
          strokeLinecap="round"
        />

        {/* Hover crosshair + dot */}
        {hover !== null && layout.points[hover] && (
          <>
            <line
              x1={layout.points[hover]!.x}
              x2={layout.points[hover]!.x}
              y1={PADDING_TOP}
              y2={height - PADDING_BOTTOM}
              stroke="var(--border-strong)"
              strokeWidth={1}
              strokeDasharray="3 3"
            />
            <circle
              cx={layout.points[hover]!.x}
              cy={layout.points[hover]!.y}
              r={4}
              fill={stroke}
              stroke="var(--bg)"
              strokeWidth={2}
            />
          </>
        )}
      </svg>

      {/* Tooltip */}
      {hover !== null && layout.points[hover] && (
        <Tooltip
          x={layout.points[hover]!.x}
          width={width}
          date={formatDate(points[hover]!.date)}
          value={formatValue(points[hover]!.value)}
          color={stroke}
        />
      )}
    </div>
  );
}

function Tooltip({
  x,
  width,
  date,
  value,
  color,
}: {
  x: number;
  width: number;
  date: string;
  value: string;
  color: string;
}) {
  // Anchor правее/левее чтобы не вылезать за края
  const tooltipWidth = 140;
  const margin = 8;
  let left = x - tooltipWidth / 2;
  if (left < margin) left = margin;
  if (left + tooltipWidth > width - margin) left = width - margin - tooltipWidth;
  const leftPct = (left / width) * 100;
  const tooltipWidthPct = (tooltipWidth / width) * 100;
  return (
    <div
      style={{ left: `${leftPct}%`, width: `${tooltipWidthPct}%`, top: 0 }}
      className="pointer-events-none absolute -translate-y-2 rounded-md border border-border bg-bg shadow-md"
    >
      <div className="border-b border-border-subtle px-2 py-1 text-[10px] font-medium uppercase tracking-wide text-text2">
        {date}
      </div>
      <div className="flex items-center gap-1.5 px-2 py-1.5">
        <span className="h-2 w-2 rounded-full" style={{ background: color }} />
        <span className="font-mono text-xs font-semibold tabular-nums text-text">{value}</span>
      </div>
    </div>
  );
}

interface Layout {
  points: { x: number; y: number }[];
  line: string;
  area: string;
  yTicks: { value: number; y: number; label: string }[];
  xTicks: { x: number; iso: string }[];
}

function buildLayout(points: ChartPoint[], width: number, height: number): Layout {
  if (points.length === 0)
    return { points: [], line: '', area: '', yTicks: [], xTicks: [] };

  const innerLeft = PADDING_LEFT;
  const innerRight = width - PADDING_RIGHT;
  const innerTop = PADDING_TOP;
  const innerBottom = height - PADDING_BOTTOM;
  const innerW = innerRight - innerLeft;
  const innerH = innerBottom - innerTop;

  const values = points.map((p) => p.value);
  let min = Math.min(...values, 0);
  let max = Math.max(...values, 0);
  if (min === max) {
    min -= 1;
    max += 1;
  }
  const pad = (max - min) * 0.08;
  min -= pad;
  max += pad;

  const xScale = (i: number) => innerLeft + (points.length > 1 ? (i / (points.length - 1)) * innerW : innerW / 2);
  const yScale = (v: number) => innerBottom - ((v - min) / (max - min)) * innerH;

  const pts = points.map((p, i) => ({ x: xScale(i), y: yScale(p.value) }));
  const line = pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(2)} ${p.y.toFixed(2)}`).join(' ');
  const area = `${line} L ${pts[pts.length - 1]!.x.toFixed(2)} ${innerBottom} L ${pts[0]!.x.toFixed(2)} ${innerBottom} Z`;

  // Y-axis: 4 тика
  const yTicks: Layout['yTicks'] = [];
  const tickCount = 4;
  for (let i = 0; i <= tickCount; i++) {
    const v = min + ((max - min) * i) / tickCount;
    yTicks.push({ value: v, y: yScale(v), label: shortNum(v) });
  }

  // X-axis: ~6 тиков, равномерно
  const xTicks: Layout['xTicks'] = [];
  const xTickCount = Math.min(6, points.length);
  for (let i = 0; i < xTickCount; i++) {
    const idx = Math.round(((points.length - 1) * i) / Math.max(xTickCount - 1, 1));
    xTicks.push({ x: xScale(idx), iso: points[idx]!.date });
  }

  return { points: pts, line, area, yTicks, xTicks };
}

function shortNum(n: number): string {
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  if (abs >= 1) return n.toFixed(0);
  return n.toFixed(2);
}

function useResizeObserver(
  ref: React.RefObject<HTMLElement | null>,
  onResize: (width: number) => void,
) {
  if (typeof window === 'undefined') return;
  // eslint-disable-next-line react-hooks/rules-of-hooks
  useResizeObserverImpl(ref, onResize);
}

function useResizeObserverImpl(
  ref: React.RefObject<HTMLElement | null>,
  onResize: (width: number) => void,
) {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const cb = useStableCallback(onResize);
  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffectOnce(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) cb(entry.contentRect.width);
    });
    ro.observe(el);
    cb(el.clientWidth);
    return () => ro.disconnect();
  });
}

import { useEffect, useCallback } from 'react';

function useEffectOnce(fn: () => void | (() => void)) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(fn, []);
}

function useStableCallback<T extends (...args: any[]) => any>(fn: T): T {
  const ref = useRef(fn);
  ref.current = fn;
  return useCallback(((...args: any[]) => ref.current(...args)) as T, []);
}
